import { type AgencyId, type SuretyAccountId } from "@inrev/common";
import { useBondRequestFlowAnalytics } from "@portal/hooks/useBondRequestFlowAnalytics";
import {
	type PendingOutcomeEntry,
	usePendingBondRequestOutcomes,
} from "@portal/providers/PendingBondRequestOutcomeProvider";
import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { EMPTY_BOND_FORM } from "../../../constants";
import { ConfirmationModal } from "../../layout/ConfirmationModal";
import { Modal } from "../../layout/Modal";
import { ModalItemWithHeader } from "../../layout/ModalItemWithHeader";
import { createFormResolver } from "../../multiform/utils/formResolver";
import { BondRequestSubmissionFlowBody } from "./BondRequestSubmissionFlowBody";
import {
	buildMinimalSubmissionFormSchema,
	type SubmissionFormData,
} from "./bondRequestSubmissionFormSchema";

type BondRequestSubmissionModalProps =
	| {
			isAdmin: true;
			accountId?: SuretyAccountId;
			agencyId?: AgencyId;
			onClose: () => void;
	  }
	| {
			isAdmin?: false;
			accountId: SuretyAccountId;
			agencyId?: AgencyId;
			onClose: () => void;
	  };

const MODAL_ITEM_CLASSNAME = "w-[600px] max-w-[90vw] max-h-[90vh] rounded-lg overflow-hidden";
const MODAL_BODY_CLASSNAME = "overflow-y-auto overscroll-contain min-h-0";

const DEFAULT_VALUES: SubmissionFormData = {
	accountId: undefined,
	agencyId: undefined,
	principal: undefined,
	bondType: undefined,
	contractBondType: undefined,
	agentId: undefined,
	projectName: "",
	submissionNarrative: "",
	scopeOfWork: [],
	completionBond: undefined,
	tListingRequired: undefined,
	bidDate: undefined,
	finalBondFormType: undefined,
	bidBondId: undefined,
	actualBidAmount: undefined,
	actualBidDate: undefined,
	secondBid: undefined,
	bondAmount: undefined,
	contractAmount: undefined,
	effectiveDate: undefined,
	bondForm: EMPTY_BOND_FORM,
};

export const BondRequestSubmissionModal: FC<BondRequestSubmissionModalProps> = ({
	isAdmin,
	accountId,
	agencyId,
	onClose,
}) => {
	const analytics = useBondRequestFlowAnalytics("modal");

	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const { watchOutcome } = usePendingBondRequestOutcomes();
	const pendingWatchRef = useRef<PendingOutcomeEntry | undefined>(undefined);

	const resolver = useMemo(
		() =>
			createFormResolver<SubmissionFormData>(buildMinimalSubmissionFormSchema(), {
				scopeToNames: true,
			}),
		[],
	);
	const formMethods = useForm<SubmissionFormData>({
		mode: "onBlur",
		resolver,
		defaultValues: { ...DEFAULT_VALUES, accountId, agencyId },
	});
	const selectedAccountId = useWatch({ control: formMethods.control, name: "accountId" });

	const { isDirty } = formMethods.formState;

	// Registers the background outcome watch (if the flow reached the waiting step) before
	// actually closing — this must run on every path that closes the modal, confirmed or not.
	const closeAndWatch = useCallback(() => {
		if (pendingWatchRef.current) {
			analytics.onWaitResolved("backgrounded");
			watchOutcome(pendingWatchRef.current);
		}
		onClose();
	}, [analytics, onClose, watchOutcome]);

	const handleRequestClose = useCallback(() => {
		if (isDirty && !isSubmitted) {
			setShowCloseConfirmation(true);
			return;
		}
		closeAndWatch();
	}, [isDirty, isSubmitted, closeAndWatch]);

	const handleSubmissionStarted = useCallback(() => setIsSubmitted(true), []);
	const handleSubmissionFailed = useCallback(() => setIsSubmitted(false), []);

	const handleWaitingStateChange = useCallback((entry: PendingOutcomeEntry | undefined) => {
		pendingWatchRef.current = entry;
	}, []);

	const body = (
		<BondRequestSubmissionFlowBody
			isAdmin={isAdmin}
			accountId={selectedAccountId}
			onClose={closeAndWatch}
			formMethods={formMethods}
			analytics={analytics}
			onSubmissionStarted={handleSubmissionStarted}
			onSubmissionFailed={handleSubmissionFailed}
			onWaitingStateChange={handleWaitingStateChange}
		/>
	);
	return (
		<>
			<Modal onClickOutside={handleRequestClose}>
				<div className="contents" onWheel={(e) => e.stopPropagation()}>
					<ModalItemWithHeader
						header="Bond Request"
						onClose={handleRequestClose}
						closeTrackingId="bond-request-modal-close"
						className={MODAL_ITEM_CLASSNAME}
						bodyClassName={MODAL_BODY_CLASSNAME}
					>
						{body}
					</ModalItemWithHeader>
				</div>
			</Modal>
			{showCloseConfirmation && (
				<ConfirmationModal
					message="Are you sure you want to close?"
					subMessage="Your changes will be lost."
					confirmButtonText="Discard Changes"
					confirmationButtonColor="danger"
					cancelButtonText="Continue Editing"
					onConfirm={() => {
						setShowCloseConfirmation(false);
						closeAndWatch();
					}}
					onCancel={() => setShowCloseConfirmation(false)}
				/>
			)}
		</>
	);
};
